// =============================================================================
// HUB OPÉRATION - Planning Google Calendar (orchestrateur)
// =============================================================================

import { useState, useEffect } from "react";
import {
  Calendar, ChevronLeft, ChevronRight, Plus, RefreshCw,
  Filter, List
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

import VisitesAMOView from "./VisitesAMOView";
import { GCalListView, GCalWeekView, GCalDayView, GCalMonthView } from "./GoogleCalendarViews";
import { CreateEventDialog, EventDetailDialog } from "./EventDialogs";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs des événements
const EVENT_COLORS = {
  // 🔴 N°737 — « Visite AMO » → « Visite », POUR TOUS LES TENANTS. Décision JJ : ce type
  // d'événement n'est PAS propre à l'AMO, c'est son NOM qui l'était. ⛔ On ⛔ ne le GATE pas,
  // on le RENOMME — et donc ⛔ pas de libellé conditionnel : UN SEUL mot pour tout le monde.
  // ⚠️ La CLÉ « 9 » et la COULEUR ⛔ ne changent pas : c'est une donnée Google Calendar
  //    partagée, déjà écrite sur les événements existants (constat de CC DEV, N°728-4, repris
  //    ici — sa mesure « la source visites reste ouverte à tous » est la même que la mienne).
  "9": { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]", label: "Visite" },
  "11": { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]", label: "Climat PAC" },
  "5": { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]", label: "Sous-traitance" },
  "10": { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]", label: "Chantier travaux" },
  "holiday": { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]", label: "Jour férié" },
  "default": { bg: "bg-slate-500/30", border: "border-[var(--oh-border)]", text: "text-[var(--oh-text-secondary)]", label: "Autre" }
};

const INSTALLATEUR_PALETTE = [
  { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  { bg: "bg-cyan-500/30", border: "border-cyan-500", text: "text-cyan-300" },
  { bg: "bg-[var(--oh-stat-red-bg)]", border: "border-[var(--oh-stat-red-border)]", text: "text-[var(--oh-stat-red-text)]" },
  { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]" },
];

function getEventInstallateur(event) {
  if (!event) return "Interne";
  if (event.installateur) return event.installateur;
  const attendees = event.attendees || [];
  if (attendees.length > 0) {
    const ext = attendees.find(a => !a.self);
    if (ext) return ext.displayName || ext.name || ext.email?.split("@")[0] || "Installateur";
  }
  return "Interne";
}

const INITIAL_FORM = {
  title: "", start_date: "", start_time: "", end_date: "", end_time: "",
  description: "", location: "", color_id: "9", type_chantier: "visite_amo",
  installateur: "", visiteur: "", all_day: false,
};

export default function PlanningGoogleCalendar({ tenantId }) {
  const { user: currentUser } = useAuth();
  // OH-0019 : "installateur" mirrored sur "external" (masquage planning identique).
  const isExternal = currentUser?.role === "external" || currentUser?.role === "installateur";
  const myInstallateurName = currentUser?.name || "";
  const [events, setEvents] = useState([]);
  const [visitesAMO, setVisitesAMO] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("calendar");
  const [dataSource, setDataSource] = useState("gcal");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterInstallateur, setFilterInstallateur] = useState("");
  const [filterVisiteur, setFilterVisiteur] = useState("");
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarName, setCalendarName] = useState("");
  const [visiteurs, setVisiteurs] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [typesRdv, setTypesRdv] = useState([]);

  const getHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // ── Data loading ────────────────────────────────────────────────────────

  useEffect(() => { testConnection(); loadVisitesAMO(); loadTypesRdv(); }, [tenantId]);
  useEffect(() => { if (dataSource === "gcal") loadEvents(); }, [dataSource, currentMonth]);
  useEffect(() => { loadEvents(); }, [currentMonth, filterInstallateur]);

  const loadVisitesAMO = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        ...enteteTenant(tenantId),
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const res = await fetch(`${API}/api/metier/visites/calendrier`, { headers, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setVisitesAMO(data.events || []);
        setVisiteurs([...new Set(data.events.map(e => e.visiteur).filter(Boolean))]);
      }
    } catch (e) {
      console.error("Erreur chargement visites AMO:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadTypesRdv = async () => {
    try {
      const res = await fetch(`${API}/api/metier/config-listes/types_rdv`, { headers: getHeaders(), credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.items?.length) {
          setTypesRdv(data.items);
          const defaultType = data.items.find(t => t.default) || data.items[0];
          if (defaultType) setForm(f => ({ ...f, type_chantier: defaultType.id, color_id: defaultType.color_id || "9" }));
        }
      }
    } catch (e) {
      console.error("Erreur chargement types RDV:", e);
    }
  };

  const testConnection = async () => {
    try {
      const res = await fetch(`${API}/api/calendar/test`, { headers: getHeaders(), credentials: "include" });
      const data = await res.json();
      setCalendarConnected(data.success);
      if (data.success) setCalendarName(data.calendar_name);
    } catch { setCalendarConnected(false); }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Bug planning chevauchement 2 mois (fix 2026-05-07) : la grille
      // mensuelle affiche la dernière semaine du mois précédent et la
      // première du mois suivant pour compléter les semaines partielles.
      // On élargit donc la fenêtre de fetch pour couvrir aussi ces
      // overflow days — sinon les RDV des 27-28 avril sont absents quand
      // on visualise mai. ~10 j en arrière, ~1 mois et demi devant.
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 21);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 7);
      const params = new URLSearchParams({
        date_from: startDate.toISOString().split("T")[0],
        date_to: endDate.toISOString().split("T")[0],
        max_results: "200"
      });
      if (filterInstallateur) params.append("installateur_email", filterInstallateur);

      const res = await fetch(`${API}/api/calendar/events?${params}`, { headers: getHeaders(), credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (e) {
      console.error("Erreur chargement événements:", e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  // ── Actions ─────────────────────────────────────────────────────────────

  const createEvent = async () => {
    if (!form.title || !form.start_date) { toast.error("Titre et date de début requis"); return; }
    try {
      let desc = form.description || "";
      if (form.installateur) desc = `Installateur: ${form.installateur}\n${desc}`;
      if (form.visiteur) desc = `Visiteur: ${form.visiteur}\n${desc}`;

      const payload = {
        title: form.title, start_date: form.start_date, start_time: form.start_time || null,
        end_date: form.end_date || form.start_date, end_time: form.end_time || form.start_time || null,
        description: desc, location: form.location, color_id: form.color_id,
        all_day: form.all_day || !form.start_time,
      };

      const res = await fetch(`${API}/api/calendar/events`, {
        method: "POST", headers: getHeaders(), credentials: "include", body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success("RDV créé !");
        setShowCreateDialog(false);
        setForm(INITIAL_FORM);
        loadEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur création");
      }
    } catch { toast.error("Erreur création événement"); }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm("Supprimer cet événement ?")) return;
    try {
      const res = await fetch(`${API}/api/calendar/events/${eventId}`, {
        method: "DELETE", headers: getHeaders(), credentials: "include"
      });
      if (res.ok) { toast.success("Événement supprimé"); loadEvents(); setSelectedEvent(null); }
      else toast.error("Erreur suppression");
    } catch { toast.error("Erreur suppression"); }
  };

  // ── Computed ────────────────────────────────────────────────────────────

  // Enrichir les événements avec isMine / isGrayed pour les installateurs
  const enrichedEvents = events.map(e => {
    if (!isExternal) return { ...e, isMine: true, isGrayed: false };
    if (e.is_holiday) return { ...e, isMine: false, isGrayed: false };
    const inst = getEventInstallateur(e);
    const isMine = inst && myInstallateurName && inst.toLowerCase().includes(myInstallateurName.toLowerCase());
    return { ...e, isMine, isGrayed: !isMine };
  });

  const installateurColorMap = {};
  let colorIdx = 0;
  enrichedEvents.forEach(e => {
    if (e.is_holiday) return;
    const inst = getEventInstallateur(e);
    if (!installateurColorMap[inst]) {
      installateurColorMap[inst] = INSTALLATEUR_PALETTE[colorIdx % INSTALLATEUR_PALETTE.length];
      colorIdx++;
    }
  });

  const getEventColor = (event) => {
    if (event.isGrayed) return { bg: "bg-[var(--oh-bg-elevated)]", border: "border-[var(--oh-border)]", text: "text-[var(--oh-text-muted)]", label: "Indisponible" };
    if (event.is_holiday) return EVENT_COLORS.holiday;
    if (event.color_id && EVENT_COLORS[event.color_id]) return EVENT_COLORS[event.color_id];
    const inst = getEventInstallateur(event);
    return installateurColorMap[inst] || EVENT_COLORS.default;
  };

  const getEventsForDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;

    return enrichedEvents.filter(e => {
      const eventStart = (e.start || "").split("T")[0];
      const eventEnd = (e.end || "").split("T")[0] || eventStart;
      if (e.all_day) return dateStr >= eventStart && dateStr < eventEnd;
      return dateStr >= eventStart && dateStr <= eventStart;
    });
  };

  // ── Navigation ──────────────────────────────────────────────────────────

  const previousMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToToday = () => setCurrentMonth(new Date());
  const monthName = currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  // ── Render ──────────────────────────────────────────────────────────────

  // Pour les installateurs, ne pas ouvrir le détail des events non-assignés
  const handleSelectEvent = (event) => {
    if (event.isGrayed) return; // Installateur : pas de détail sur les events des autres
    setSelectedEvent(event);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-[var(--oh-info-text)] animate-spin" />
        </div>
      );
    }

    if (dataSource === "visites") {
      return (
        <VisitesAMOView
          visites={visitesAMO}
          filterVisiteur={filterVisiteur}
          viewMode={viewMode}
          currentMonth={currentMonth}
          onSelectVisite={(v) => window.location.href = `?hub=commercial&item=dossier-detail&id=${v.id}`}
          isExternal={isExternal}
          currentUserName={myInstallateurName}
        />
      );
    }

    // Google Calendar views
    if (viewMode === "list") {
      return <GCalListView events={enrichedEvents} getEventColor={getEventColor} onSelectEvent={handleSelectEvent} onDeleteEvent={deleteEvent} />;
    }
    if (viewMode === "week") {
      return <GCalWeekView selectedDate={selectedDate} setSelectedDate={setSelectedDate} setViewMode={setViewMode} getEventsForDate={getEventsForDate} getEventColor={getEventColor} onSelectEvent={handleSelectEvent} />;
    }
    if (viewMode === "day") {
      return <GCalDayView selectedDate={selectedDate} setSelectedDate={setSelectedDate} getEventsForDate={getEventsForDate} getEventColor={getEventColor} onSelectEvent={handleSelectEvent} />;
    }
    return (
      <GCalMonthView
        currentMonth={currentMonth} events={enrichedEvents} getEventsForDate={getEventsForDate}
        getEventColor={getEventColor} onSelectEvent={handleSelectEvent}
        onSelectDate={(date) => { setSelectedDate(date); setViewMode("day"); }}
      />
    );
  };

  return (
    <div className="space-y-6" data-testid="planning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)] flex items-center gap-3">
            <Calendar className="w-7 h-7 text-[var(--oh-info-text)]" />
            Planning Interventions
          </h1>
          <div className="text-[var(--oh-text-secondary)] mt-1 flex items-center gap-2">
            {calendarConnected ? (
              <>
                <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">Google Calendar</Badge>
                <span className="text-sm">{calendarName}</span>
              </>
            ) : (
              <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]">Non connecté</Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="border-[var(--oh-border)]" onClick={loadEvents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {!isExternal && (
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setShowCreateDialog(true)} data-testid="new-rdv-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau RDV
            </Button>
          )}
      </div>

      {/* Navigation et filtres */}
        </div>
      <div className="flex items-center justify-between bg-[var(--oh-bg-elevated)] rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="flex border border-[var(--oh-border)] rounded-md overflow-hidden mr-4">
            <Button variant="ghost" size="sm" onClick={() => setDataSource("visites")}
              className={`rounded-none h-8 px-3 ${dataSource === "visites" ? "bg-orange-600 text-white" : "text-[var(--oh-text-secondary)]"}`}>
              {/* 🔴 DÉCISION JJ (N°737/742) : ce bloc n'est PAS AMO, c'est son NOM qui
                  l'était. Renommé pour TOUS les tenants — ⛔ il n'est PAS gaté. */}
              Visites
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDataSource("gcal")}
              className={`rounded-none h-8 px-3 ${dataSource === "gcal" ? "bg-[var(--oh-accent-solid)] text-white" : "text-[var(--oh-text-secondary)]"}`}>
              Google Cal
            </Button>
          </div>

          {/* CC-34 — Layout GCal natif : Aujourd'hui à gauche, mois centré
              entre les flèches < / >. */}
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            className="text-[var(--oh-text-secondary)] hover:text-white border-[var(--oh-border)] mr-3"
            data-testid="cal-today-btn"
            aria-label="Revenir au mois courant"
          >
            Aujourd'hui
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={previousMonth}
            className="text-[var(--oh-text-secondary)] hover:text-white"
            data-testid="cal-prev-month"
            aria-label="Mois précédent"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span
            className="text-[var(--oh-text)] font-medium capitalize px-2 min-w-[120px] text-center"
            data-testid="cal-current-month"
          >
            {monthName}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={nextMonth}
            className="text-[var(--oh-text-secondary)] hover:text-white"
            data-testid="cal-next-month"
            aria-label="Mois suivant"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex items-center gap-3">
          {dataSource === "visites" && visiteurs.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[var(--oh-text-secondary)]" />
              <select
                value={filterVisiteur}
                onChange={(e) => setFilterVisiteur(e.target.value)}
                className="h-8 bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded text-sm text-[var(--oh-text)] px-2"
                data-testid="filter-visiteur"
              >
                <option value="">Tous les visiteurs</option>
                {visiteurs.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          )}

          {dataSource === "gcal" && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[var(--oh-text-secondary)]" />
              <Input
                placeholder="Filtrer par installateur..."
                value={filterInstallateur}
                onChange={(e) => setFilterInstallateur(e.target.value)}
                className="w-48 h-8 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-sm"
                data-testid="filter-installateur"
              />
            </div>
          )}

          <div className="flex border border-[var(--oh-border)] rounded-md overflow-hidden">
            {["day", "week", "calendar", "list"].map((mode) => (
              <Button
                key={mode}
                variant="ghost" size="sm"
                onClick={() => setViewMode(mode)}
                className={`rounded-none h-8 px-2 text-xs ${viewMode === mode ? "bg-[var(--oh-bg-elevated)] text-[var(--oh-text)]" : "text-[var(--oh-text-secondary)]"}`}
              >
                {mode === "day" ? "Jour" : mode === "week" ? "Semaine" : mode === "calendar" ? "Mois" : <List className="w-4 h-4" />}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      {renderContent()}

      {/* Dialogs */}
      <CreateEventDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        form={form}
        setForm={setForm}
        onSubmit={createEvent}
        typesRdv={typesRdv}
      />
      <EventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        getEventColor={getEventColor}
        onDeleteEvent={deleteEvent}
      />
    </div>
  );
}
